import { useQuery } from '@apollo/client';

import type { AuthorizeInput } from '#@/__gql__/graphql';
import { queryAuth } from '#@/api';
import { isDoneLoading } from '#@/utils/apollo';
import { typeSafeFromEntries, typeSafeObjectKeys, typeSafeObjectValues } from '#@/utils/typescript';

interface CommonResults<InputNames extends string> {
  results: {
    [K in InputNames]: boolean;
  };
  refetch: () => Promise<unknown>;
}

interface SuccessStateResults {
  loading: false;
  error?: never;
}

interface LoadingStateResults {
  loading: true;
  error?: never;
}

interface ErrorStateResults {
  loading: false;
  error: Error;
}

type AuthResults<InputNames extends string> = CommonResults<InputNames> &
  (SuccessStateResults | LoadingStateResults | ErrorStateResults);

export const useAuth = <InputNames extends string>(
  inputs: Record<InputNames, AuthorizeInput | false | undefined | null>,
  options = { cacheResults: true },
): AuthResults<InputNames> => {
  const areAllInputsFalsy = typeSafeObjectValues(inputs).every((input) => !input);
  const queryResults = useQuery(queryAuth, {
    variables: { inputs: typeSafeObjectValues(inputs).map((input) => input || null) },
    ...(options.cacheResults && {
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-first',
    }),
    skip: areAllInputsFalsy,
  });

  if (areAllInputsFalsy) {
    const results = typeSafeFromEntries(typeSafeObjectKeys(inputs).map((key) => [key, false]));

    return {
      results,
      loading: false,
      refetch: () => Promise.resolve(),
    };
  }

  if (!isDoneLoading(queryResults)) {
    const results = typeSafeFromEntries(typeSafeObjectKeys(inputs).map((key) => [key, false]));

    return {
      results,
      loading: true,
      refetch: queryResults.refetch,
    };
  }

  if (queryResults.error) {
    const results = typeSafeFromEntries(typeSafeObjectKeys(inputs).map((key) => [key, false]));

    return {
      results,
      loading: false,
      error: queryResults.error,
      refetch: queryResults.refetch,
    };
  }

  const results = typeSafeFromEntries(
    typeSafeObjectKeys(inputs).map((key, i) => [
      key,
      queryResults.data.authorizeMultiple[i] || false,
    ]),
  );

  return {
    results,
    loading: false,
    refetch: queryResults.refetch,
  };
};
